import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ClientLocation } from "@/lib/location/types";

/**
 * CRUD hook for client_locations table.
 */
export function useClientLocations(clientId?: string) {
  const [locations, setLocations] = useState<ClientLocation[]>([]);
  const [loading, setLoading] = useState(true);

  const loadLocations = useCallback(async () => {
    if (!clientId) {
      setLocations([]);
      setLoading(false);
      return;
    }

    try {
      const { data } = await supabase
        .from("client_locations")
        .select("*")
        .eq("client_id", clientId)
        .order("is_primary", { ascending: false })
        .order("created_at");

      setLocations((data as ClientLocation[]) || []);
    } catch (error) {
      console.error("Error loading client locations:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const addLocation = useCallback(
    async (location: Partial<ClientLocation>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !clientId) return null;

      const { data, error } = await supabase
        .from("client_locations")
        .insert({
          client_id: clientId,
          user_id: user.id,
          ...location,
        })
        .select()
        .single();

      if (error) throw error;
      const newLoc = data as ClientLocation;
      setLocations((prev) => [...prev, newLoc]);

      // Trigger geocoding if address provided but no coordinates
      if (newLoc.street_address && !newLoc.latitude) {
        const address = [
          newLoc.street_address,
          newLoc.city,
          newLoc.state,
          newLoc.zip_code,
        ]
          .filter(Boolean)
          .join(", ");

        supabase.functions
          .invoke("geocode-address", {
            body: { address, location_id: newLoc.id, table: "client_locations" },
          })
          .then(() => loadLocations());
      }

      return newLoc;
    },
    [clientId, loadLocations]
  );

  const updateLocation = useCallback(
    async (id: string, updates: Partial<ClientLocation>) => {
      const { data, error } = await supabase
        .from("client_locations")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      const updated = data as ClientLocation;
      setLocations((prev) => prev.map((l) => (l.id === id ? updated : l)));

      // Re-geocode if address changed but no new coordinates
      if (
        (updates.street_address || updates.city || updates.state || updates.zip_code) &&
        !updates.latitude
      ) {
        const address = [
          updated.street_address,
          updated.city,
          updated.state,
          updated.zip_code,
        ]
          .filter(Boolean)
          .join(", ");

        supabase.functions
          .invoke("geocode-address", {
            body: { address, location_id: id, table: "client_locations" },
          })
          .then(() => loadLocations());
      }

      return updated;
    },
    [loadLocations]
  );

  const deleteLocation = useCallback(async (id: string) => {
    const { error } = await supabase
      .from("client_locations")
      .delete()
      .eq("id", id);

    if (error) throw error;
    setLocations((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const setPrimary = useCallback(
    async (id: string) => {
      if (!clientId) return;

      // Unset all primary flags for this client
      await supabase
        .from("client_locations")
        .update({ is_primary: false })
        .eq("client_id", clientId);

      // Set the new primary
      await supabase
        .from("client_locations")
        .update({ is_primary: true })
        .eq("id", id);

      setLocations((prev) =>
        prev.map((l) => ({ ...l, is_primary: l.id === id }))
      );
    },
    [clientId]
  );

  const primaryLocation = locations.find((l) => l.is_primary) || locations[0] || null;

  return {
    locations,
    primaryLocation,
    loading,
    addLocation,
    updateLocation,
    deleteLocation,
    setPrimary,
    reload: loadLocations,
  };
}
